import { createFileRoute, Link } from "@tanstack/react-router";
import { buildHead, PAGE_SEO, pickLang, type SeoLang } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { fetchProjects } from "@/lib/projects";
import { formatPrice, type Currency } from "@/lib/format";
import { Building2 } from "lucide-react";

type Copy = {
  h1: string;
  intro: string;
  s1: string;
  s1p: string;
  s2: string;
  s2items: string[];
  s3: string;
  s3p: string;
  cta: string;
  ctaP1: string;
  ctaLink1: string;
  ctaOr: string;
  ctaLink2: string;
};

const COPY: Record<SeoLang, Copy> = {
  es: {
    h1: "Proyectos y emprendimientos",
    intro:
      "Además de la cartera de propiedades usadas, trabajo con desarrolladoras de Asunción y Central en la comercialización de emprendimientos: departamentos en pozo, en construcción y unidades terminadas listas para escriturar.",
    s1: "Comprar en pozo",
    s1p: "Comprar antes de la finalización de la obra suele implicar un precio por metro cuadrado menor al de una unidad terminada, con planes de pago en cuotas durante la construcción y un saldo al momento de la entrega. A cambio, asumís el plazo de obra: por eso importa revisar la trayectoria de la desarrolladora, el fideicomiso o la figura legal del proyecto y las condiciones de entrega establecidas en el contrato.",
    s2: "Qué reviso antes de recomendarte un proyecto",
    s2items: [
      "Antecedentes y obras entregadas por la desarrolladora.",
      "Situación del terreno: título, planos aprobados y permisos municipales.",
      "Plan de pagos, moneda, ajustes y penalidades por atraso.",
      "Superficie propia, expensas estimadas y amenities realmente incluidos.",
      "Zona y proyección: accesos, servicios y demanda de alquiler.",
    ],
    s3: "Zonas donde trabajo emprendimientos",
    s3p: "Villa Morra, Recoleta, Las Lomas, Carmelitas, Mburucuyá y el eje corporativo de Asunción, además de desarrollos en Luque, Fernando de la Mora, Lambaré y San Bernardino.",
    cta: "¿Buscás un proyecto puntual?",
    ctaP1:
      "Contame qué zona, presupuesto y plazo manejás y te paso las unidades disponibles que encajan. Mientras tanto, podés ver la cartera de",
    ctaLink1: "departamentos en venta",
    ctaOr: "o",
    ctaLink2: "casas en venta",
  },
  en: {
    h1: "New developments and projects",
    intro:
      "Beyond resale listings, I work with developers in Asunción and the Central department on new developments: off-plan apartments, units under construction and finished units ready for title transfer.",
    s1: "Buying off-plan",
    s1p: "Buying before completion usually means a lower price per square metre than a finished unit, with instalments during construction and a balance due on delivery. In exchange you take on the construction timeline, so it matters to review the developer's track record, the trust or legal structure behind the project and the delivery terms written into the contract.",
    s2: "What I check before recommending a project",
    s2items: [
      "The developer's track record and previously delivered buildings.",
      "Land status: title, approved plans and municipal permits.",
      "Payment plan, currency, adjustments and late-delivery penalties.",
      "Private area, estimated maintenance fees and amenities actually included.",
      "Neighbourhood outlook: access, services and rental demand.",
    ],
    s3: "Where I work with developments",
    s3p: "Villa Morra, Recoleta, Las Lomas, Carmelitas, Mburucuyá and the corporate corridor of Asunción, plus projects in Luque, Fernando de la Mora, Lambaré and San Bernardino.",
    cta: "Looking for a specific project?",
    ctaP1:
      "Tell me the area, budget and timeline you have in mind and I will send the available units that fit. In the meantime, you can browse",
    ctaLink1: "apartments for sale",
    ctaOr: "or",
    ctaLink2: "houses for sale",
  },
  pt: {
    h1: "Projetos e empreendimentos",
    intro:
      "Além da carteira de imóveis usados, trabalho com incorporadoras de Assunção e do departamento Central na comercialização de empreendimentos: apartamentos na planta, em construção e unidades prontas para escritura.",
    s1: "Comprar na planta",
    s1p: "Comprar antes da conclusão da obra costuma significar um preço por metro quadrado menor do que o de uma unidade pronta, com pagamento parcelado durante a construção e um saldo na entrega. Em troca, você assume o prazo da obra: por isso é importante avaliar o histórico da incorporadora, o fideicomisso ou a estrutura jurídica do projeto e as condições de entrega previstas em contrato.",
    s2: "O que avalio antes de recomendar um projeto",
    s2items: [
      "Histórico e obras já entregues pela incorporadora.",
      "Situação do terreno: título, plantas aprovadas e alvarás municipais.",
      "Plano de pagamento, moeda, reajustes e multas por atraso.",
      "Área privativa, condomínio estimado e comodidades realmente incluídas.",
      "Região e projeção: acessos, serviços e demanda de locação.",
    ],
    s3: "Regiões onde atuo com empreendimentos",
    s3p: "Villa Morra, Recoleta, Las Lomas, Carmelitas, Mburucuyá e o eixo corporativo de Assunção, além de projetos em Luque, Fernando de la Mora, Lambaré e San Bernardino.",
    cta: "Procura um projeto específico?",
    ctaP1:
      "Conte qual região, orçamento e prazo você tem em mente e eu envio as unidades disponíveis que se encaixam. Enquanto isso, veja a carteira de",
    ctaLink1: "apartamentos à venda",
    ctaOr: "ou",
    ctaLink2: "casas à venda",
  },
};

export const Route = createFileRoute("/_public/proyectos/")({
  head: ({ match }) => {
    const lang = pickLang(match.search);
    const copy = PAGE_SEO["proyectos"]![lang];
    return buildHead({ lang, path: "/proyectos", ...copy });
  },

  loader: () => fetchProjects(),
  component: ProyectosPage,
});

function ProyectosPage() {
  const { lang } = useI18n();
  const c = COPY[lang];

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 md:px-6">
      <h1 className="font-serif text-4xl text-foreground md:text-5xl">{c.h1}</h1>
      <p className="mt-4 text-muted-foreground">{c.intro}</p>

      <ProjectGrid />

      <div className="mt-10 space-y-8">
        <section>
          <h2 className="text-xl font-semibold text-foreground">{c.s1}</h2>
          <p className="mt-2 text-muted-foreground">{c.s1p}</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">{c.s2}</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
            {c.s2items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground">{c.s3}</h2>
          <p className="mt-2 text-muted-foreground">{c.s3p}</p>
        </section>
      </div>

      <div className="mt-12 rounded-2xl bg-card p-6 shadow-card">
        <h2 className="font-serif text-2xl text-foreground">{c.cta}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {c.ctaP1}{" "}
          <Link to="/$operation/$type" params={{ operation: "venta", type: "departamentos" }} className="underline">
            {c.ctaLink1}
          </Link>{" "}
          {c.ctaOr}{" "}
          <Link to="/$operation/$type" params={{ operation: "venta", type: "casas" }} className="underline">
            {c.ctaLink2}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function ProjectGrid() {
  const projects = Route.useLoaderData();
  const { lang } = useI18n();
  const visible = projects?.filter((p) => p.units > 5) ?? [];
  if (!visible.length) return null;

  const label =
    lang === "en" ? "Available projects" : lang === "pt" ? "Projetos disponíveis" : "Proyectos disponibles";
  const fromLabel = lang === "en" ? "From" : lang === "pt" ? "A partir de" : "Desde";

  return (
    <section className="mt-12">
      <h2 className="text-xl font-semibold text-foreground">{label}</h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((p) => (
          <Link
            key={p.slug}
            to="/proyectos/$slug"
            params={{ slug: p.slug }}
            className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <div className="flex items-center gap-2 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <h3 className="mt-3 font-serif text-xl text-foreground group-hover:text-primary">{p.name}</h3>
            <div className="mt-3 space-y-1 text-sm text-muted-foreground">
              {p.minPrice ? (
                <p>
                  {fromLabel}{" "}
                  <span className="font-semibold text-foreground">
                    {formatPrice(p.minPrice, p.currency as Currency)}
                  </span>
                </p>
              ) : null}
              {p.minArea && p.maxArea ? (
                <p>
                  {Math.round(p.minArea)} - {Math.round(p.maxArea)} m²
                </p>
              ) : null}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
